import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

interface Reply {
  content: string;
  is_staff: boolean;
  created_at: string;
}

interface TicketData {
  request: {
    id: string;
    request_code: string;
    name: string;
    subject: string;
    message: string;
    status: string;
    created_at: string;
  };
  replies: Reply[];
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  open: { label: 'Đang xử lý', cls: 'bg-accent-500/15 text-accent-400' },
  answered: { label: 'Đã trả lời', cls: 'bg-emerald-500/15 text-emerald-400' },
  resolved: { label: 'Đã giải quyết', cls: 'bg-secondary-500/15 text-secondary-400' },
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function CheckRequest() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState<TicketData | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setNotFound(false);
    setError('');
    setTicket(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: code.trim() });
      if (rpcError) throw rpcError;
      if (!data) {
        setNotFound(true);
      } else {
        setTicket(data as TicketData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-3xl px-4 md:px-6 py-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Kiểm tra yêu cầu hỗ trợ</h1>
            <p className="mt-2 text-foreground-600">Nhập mã yêu cầu bạn đã nhận để xem trạng thái và câu trả lời.</p>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-4 md:px-6 py-10">
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-8">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Nhập mã yêu cầu (VD: AB12CD34)"
              className="flex-1 px-4 py-3 rounded-md bg-background-100 border border-background-300/60 text-sm font-mono tracking-widest text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 uppercase"
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="px-6 py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
            >
              {loading ? 'Đang tra cứu...' : 'Tra cứu'}
            </button>
          </form>

          {error && (
            <div className="mb-6 rounded-lg bg-rose-500/15 border border-rose-500/30 p-4 flex items-center gap-3">
              <i className="ri-error-warning-line text-rose-400 text-xl"></i>
              <p className="text-sm text-rose-400">{error}</p>
            </div>
          )}

          {notFound && (
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-8 text-center">
              <i className="ri-search-eye-line text-4xl text-foreground-500"></i>
              <p className="mt-3 text-foreground-950 font-semibold">Không tìm thấy yêu cầu</p>
              <p className="mt-1 text-sm text-foreground-600">Vui lòng kiểm tra lại mã yêu cầu đã nhập.</p>
            </div>
          )}

          {ticket && (
            <div className="space-y-6">
              <div className="rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-background-200/60 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-sm text-foreground-600">Mã yêu cầu</p>
                    <p className="font-mono text-lg font-bold tracking-widest text-foreground-950">{ticket.request.request_code}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusMeta[ticket.request.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}>
                    {statusMeta[ticket.request.status]?.label || ticket.request.status}
                  </span>
                </div>
                <div className="px-5 py-4">
                  <h2 className="text-lg font-bold text-foreground-950">{ticket.request.subject}</h2>
                  <p className="mt-1 text-sm text-foreground-600">{formatDate(ticket.request.created_at)}</p>
                  <div className="mt-3 rounded-lg bg-background-50 border border-background-200/60 p-4">
                    <p className="text-sm text-foreground-800 whitespace-pre-wrap">{ticket.request.message}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-bold text-foreground-950 mb-3">Phản hồi ({ticket.replies.length})</h3>
                {ticket.replies.length === 0 ? (
                  <div className="rounded-xl border border-background-200/60 bg-background-100 p-6 text-center">
                    <p className="text-sm text-foreground-600">Chưa có phản hồi nào. Vui lòng kiên nhẫn chờ đợi.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ticket.replies.map((r, i) => (
                      <div key={i} className={`rounded-xl p-4 border ${r.is_staff ? 'bg-primary-500/10 border-primary-500/20' : 'bg-background-100 border-background-200/60'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${r.is_staff ? 'bg-primary-500/20 text-primary-400' : 'bg-foreground-200/40 text-foreground-700'}`}>
                            {r.is_staff ? 'Hỗ trợ viên' : 'Bạn'}
                          </span>
                          <span className="text-xs text-foreground-500">{formatDate(r.created_at)}</span>
                        </div>
                        <p className="text-sm text-foreground-800 whitespace-pre-wrap">{r.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <Link to="/ho-tro" className="text-sm text-primary-400 hover:text-primary-300 font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-left-line mr-1"></i> Quay lại trung tâm hỗ trợ
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}